import React from 'react';
import { Flex, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import { width } from 'styled-system';
import gql from 'graphql-tag';
// eslint-disable-next-line import/no-unresolved
import { useQuery } from '@apollo/react-hooks';
import { inject } from 'mobx-react';
import _ from 'lodash';
import Imgix from './imgix';

import { removeTypename } from '../helpers/utils';
import { mediaQueries } from '../styles';
import { paymentMethodsToDisplayNames } from '../constants';
import { isWeightedProduct } from '../helpers/products';
import { getDefaultRewardsProgramDisplayName } from '../core/helpers/dispensaries';
import { getCheckoutLineItemsOrder } from '../helpers/checkout-line-items-order';

import { VisuallyHidden } from './visually-hidden';

export const SubTotal = ({ order, fontSize = '13px', color = '#6d747b' }) => (
  <AddCharge
    flexDirection='row'
    width={['100%', '100%', '400px', '100%']}
    justifyContent='space-between'
    alignItems='center'
    mx='auto'
  >
    <Text fontSize={fontSize} color={color}>
      Subtotal:
    </Text>
    <Text data-testid='order-subtotal' fontSize={fontSize} color={color} fontWeight='bolder'>
      {order.subtotal
        ? `$${parseFloat(order.subtotal).toFixed(2)}`
        : `$${Object.keys(order.orders || {})
            .map((o) => order.orders[o].price)
            .reduce((a, b) => a + b, 0)
            .toFixed(2)}`}
    </Text>
  </AddCharge>
);

const getTaxExistence = (medicalOrder, taxExistence) => removeTypename(taxExistence?.[medicalOrder ? 'med' : 'rec']);

const getTaxText = (doesChargeExist, chargeValue) =>
  doesChargeExist && parseFloat(chargeValue) > 0 ? `$${parseFloat(chargeValue).toFixed(2)}` : 'Included';

/**
 * when a dispo has taxesFirst set in their config, the taxes are rolled into the subtotal
 * so the taxes do exist but they're reduced to 0 for the receipt, in this case we want
 * to show taxes 'included'
 */
const shouldTaxDisplay = (taxExistence) => taxExistence && _.some(_.values(taxExistence), (exists) => !!exists);

const getTaxDisplayConfiguration = (medicalOrder, taxExistence, taxValue) => {
  const updatedTaxExistence = getTaxExistence(medicalOrder, taxExistence);

  return {
    taxExistence: updatedTaxExistence,
    displayTax: shouldTaxDisplay(updatedTaxExistence),
    taxText: getTaxText(!!updatedTaxExistence, taxValue),
  };
};

const renderCheckoutLineItemsCharges = ({
  checkoutLineItems,
  fontSize,
  color,
  rewardsDisplayName,
  excludePaymentFee,
  paymentMethod,
  dispensary,
}) => {
  // Get display order based on dispensary tax configuration
  const displayOrder = getCheckoutLineItemsOrder(dispensary);

  const hideSalesLanguage = dispensary?.storeSettings?.restrictSalesLanguage;

  const lineItemDisplayMap = {
    bottleDepositTaxCents: 'Bottle Deposit Taxes',
    cannabisTax: 'Cannabis Taxes',
    deliveryFee: 'Delivery Fee',
    mixAndMatch: 'Mix and Match',
    otherFees: 'Other Fees',
    paymentFee: 'Payment Fee',
    salesTax: 'Sales Taxes',
    tip: 'Tip',
    vapourTax: 'Vapour Taxes',
    discount: hideSalesLanguage ? 'Offers' : 'Discount',
    reward: rewardsDisplayName || 'Rewards',
    roundupForCharity: 'Round up donation',
  };

  const formatValue = (type, value) => {
    switch (type) {
      case 'mixAndMatch':
      case 'discount':
      case 'reward':
        return `- $${parseFloat(value).toFixed(2)}`;
      default:
        // All values from checkoutLineItems are already in dollars
        return `$${parseFloat(value).toFixed(2)}`;
    }
  };

  const shouldDisplayItem = (type, value) => {
    if (excludePaymentFee && type === 'paymentFee') {
      return false;
    }

    // Don't show these items as they're handled elsewhere
    if (['productSubtotal', 'total', 'totalTax', 'fees'].includes(type)) {
      return false;
    }

    // Only show items that have a value > 0
    return value > 0;
  };

  const getDisplayName = (type) => {
    if (type === 'paymentFee') {
      const methodName = paymentMethodsToDisplayNames[paymentMethod] || 'Payment';
      return `${methodName} Fee`;
    }
    return lineItemDisplayMap[type] || type;
  };

  // Sort line items according to display order
  const sortedLineItems = _.sortBy(
    checkoutLineItems.filter((item) => shouldDisplayItem(item.type, item.value)),
    (item) => {
      const index = displayOrder.indexOf(item.type);
      return index === -1 ? displayOrder.length : index;
    }
  );
  return (
    <>
      {sortedLineItems.map((item) => {
        const displayValue = formatValue(item.type, item.value);
        const dataTestId = `order-${item.type}`;

        return (
          <AddCharge
            key={item.type}
            flexDirection='row'
            width={['100%', '100%', '400px', '100%']}
            justifyContent='space-between'
            alignItems='center'
            mx='auto'
          >
            <Text fontSize={fontSize} color={color}>
              {getDisplayName(item.type)}:
            </Text>
            <Text data-testid={dataTestId} fontSize={fontSize} color={color} fontWeight='bolder'>
              {displayValue}
            </Text>
          </AddCharge>
        );
      })}
    </>
  );
};

export const AdditionalCharges = ({
  order,
  fontSize = '13px',
  color = '#6d747b',
  rewardsDisplayName,
  excludePaymentFee = false,
}) => {
  const {
    medicalOrder,
    deliveryFee,
    bottleDepositTaxCents,
    cannabisTax,
    taxAmount,
    tipAmount,
    paymentFee,
    paymentMethod,
    credit,
    receipt,
    checkoutLineItems,
    dispensary,
  } = order;

  if (checkoutLineItems) {
    return renderCheckoutLineItemsCharges({
      checkoutLineItems,
      fontSize,
      color,
      rewardsDisplayName,
      excludePaymentFee,
      paymentMethod,
      dispensary,
    });
  }

  const taxBreakdown = receipt?.details?.taxBreakdown;
  const vapourTax = (taxBreakdown?.vape ?? 0) / 100;
  const salesTaxAmount = taxAmount - vapourTax;
  // since vapourTax is a subset of sales tax, sales tax might "exist" but be completely comprised of vape taxed products
  // in this case we will hide the sales tax line and only show the vapour tax line
  const hideSalesTaxLineItem = !!vapourTax && taxAmount === vapourTax;

  const {
    taxExistence: salesTaxExistence,
    displayTax: displaySalesTax,
    taxText: salesTaxToDisplay,
  } = getTaxDisplayConfiguration(medicalOrder, order?.salesTaxExistence, salesTaxAmount);
  const {
    taxExistence: cannabisTaxExistence,
    displayTax: displayCannabisTax,
    taxText: cannabisTaxToDisplay,
  } = getTaxDisplayConfiguration(medicalOrder, order?.cannabisTaxExistence, cannabisTax);
  const {
    taxExistence: bottleDepositTaxExistence,
    displayTax: displayBottleDepositTax,
    taxText: bottleDepositTaxToDisplay,
  } = getTaxDisplayConfiguration(medicalOrder, order?.bottleDepositTaxExistence, bottleDepositTaxCents / 100);

  const displayVapourTax = !!vapourTax;
  const vapourTaxToDisplay = getTaxText(displayVapourTax, vapourTax);

  const mixAndMatch = order?.mixAndMatch || 0;

  const hasAppliedRewards = order?.appliedRewards?.length > 0;
  let reward = null;
  if (hasAppliedRewards) {
    [reward] = order.appliedRewards;
  }

  const otherFees = receipt.details.order.fees?.other;
  const roundupForCharity = receipt?.summary?.roundupForCharity ?? 0;

  return (
    <>
      {mixAndMatch > 0 && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Mix and Match:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>{`($${parseFloat(mixAndMatch).toFixed(
            2
          )})`}</Text>
        </AddCharge>
      )}
      {order.deliveryFee > 0 && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Delivery Fee:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>{`$${parseFloat(deliveryFee).toFixed(2)}`}</Text>
        </AddCharge>
      )}
      {otherFees && otherFees > 0 && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Other Fees:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>{`$${(otherFees / 100).toFixed(2)}`}</Text>
        </AddCharge>
      )}
      {displayBottleDepositTax && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Bottle Deposit Taxes:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>
            {bottleDepositTaxExistence ? bottleDepositTaxToDisplay : 'Not Included'}
          </Text>
        </AddCharge>
      )}
      {displayCannabisTax && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Cannabis Taxes:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>
            {cannabisTaxExistence ? cannabisTaxToDisplay : 'Not Included'}
          </Text>
        </AddCharge>
      )}
      {displaySalesTax && !hideSalesTaxLineItem && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Sales Taxes:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>
            {salesTaxExistence ? salesTaxToDisplay : 'Not Included'}
          </Text>
        </AddCharge>
      )}
      {displayVapourTax && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Vapour Taxes:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>
            {vapourTaxToDisplay}
          </Text>
        </AddCharge>
      )}
      {roundupForCharity > 0 && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Round up donation:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>{`$${(roundupForCharity / 100).toFixed(
            2
          )}`}</Text>
        </AddCharge>
      )}
      {tipAmount > 0 && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Tip:
          </Text>
          <Text data-testid='order-tip' fontSize={fontSize} color={color} fontWeight='bolder'>
            {`$${parseFloat(tipAmount).toFixed(2)}`}
          </Text>
        </AddCharge>
      )}
      {paymentFee > 0 && !excludePaymentFee && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            {paymentMethod in paymentMethodsToDisplayNames ? paymentMethodsToDisplayNames[paymentMethod] : 'Payment'}{' '}
            Fee:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>{`$${(paymentFee / 100).toFixed(2)}`}</Text>
        </AddCharge>
      )}
      {credit > 0 && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            Credit:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>{`($${parseFloat(credit).toFixed(2)})`}</Text>
        </AddCharge>
      )}
      {hasAppliedRewards && (
        <AddCharge
          flexDirection='row'
          width={['100%', '100%', '400px', '100%']}
          justifyContent='space-between'
          alignItems='center'
          mx='auto'
        >
          <Text fontSize={fontSize} color={color}>
            {rewardsDisplayName}:
          </Text>
          <Text fontSize={fontSize} color={color} fontWeight='bolder'>
            {reward.operator === '$' && `($${parseFloat(reward.value).toFixed(2)})`}
            {reward.operator === '%' && `($${parseFloat(reward.value * 0.01 * parseFloat(order.subtotal)).toFixed(2)})`}
          </Text>
        </AddCharge>
      )}
    </>
  );
};

export const Total = ({ order, uppercase = true, fontSize = '14px' }) => (
  <Flex
    flexDirection='row'
    width={['100%', '100%', '400px', '100%']}
    justifyContent='space-between'
    alignItems='center'
    mt='25px'
    mx='auto'
  >
    <Text fontSize={fontSize} color='#46494c' fontWeight='bolder'>
      {uppercase ? 'ORDER TOTAL:' : 'Order Total:'}
    </Text>
    <Text data-testid='order-total' fontSize={fontSize} color='#46494c' fontWeight='bolder'>{`$${parseFloat(
      order.totalCost || 0
    ).toFixed(2)}`}</Text>
  </Flex>
);

const DispoQuery = gql`
  query GetLogoForReceipt($dispensaryFilter: dispensariesFilterInput!) {
    filteredDispensaries(filter: $dispensaryFilter) {
      logoImage
      storeSettings {
        rewardsIntegrationConfiguration {
          rewardsProgramDisplayName
        }
      }
      location {
        country
      }
    }
  }
`;

export const Receipt = inject('apolloClient')(({ order, dispensaryUrl, apolloClient, checkoutLineItems }) => {
  const { data } = useQuery(DispoQuery, {
    variables: {
      dispensaryFilter: {
        cNameOrID: order.dispensaryId || order.dispensary._id,
      },
    },
    client: apolloClient,
  });
  const dispensary = data?.filteredDispensaries?.[0] ?? {};
  const rewardsDisplayName =
    dispensary?.storeSettings?.rewardsIntegrationConfiguration?.rewardsProgramDisplayName ??
    getDefaultRewardsProgramDisplayName(dispensary);
  const logoImage = dispensary?.logoImage;

  return (
    <>
      <LogoContainer>
        {!_.isEmpty(logoImage) && (
          <a data-cy='dispensary-logo-link' data-testid='dispensary-logo-link' href={dispensaryUrl}>
            <VisuallyHidden>{`Go to${order.dispensary.name} homepage`}</VisuallyHidden>
            <Logo role='img' title='dispensary logo' height={69} fit='clip' src={logoImage} />
          </a>
        )}
        <Flex flexDirection='column'>
          <Text data-testid='dispensary-name' color='#6d747b' fontSize='18px' fontWeight='bold' mb='10px' mt='5px'>
            {order.dispensary.name}
          </Text>
          <Text data-testid='dispensary-address' color='#6d747b' fontSize='13px' lineHeight='1.5'>
            {order.dispensary.address}
          </Text>
        </Flex>
      </LogoContainer>

      <Divider width={['100%', '100%', '400px', '100%']} />
      <Flex flexDirection='column' width={['100%', '100%', '400px', '100%']} mb='17px' mx='auto'>
        <Flex flexDirection='row' alignItems='center' justifyContent='space-between' width='100%' mt='10px'>
          <Text fontSize='14px' color='#6d747b' fontWeight='bold'>{`(${order.orders.length}) Item${
            order.orders.length > 1 ? 's' : ''
          }`}</Text>
          <Text fontSize='13px' color='#a7aeb4' fontWeight='bold'>{`ORDER: ${order.orderId}`}</Text>
        </Flex>
      </Flex>
      <Flex flexDirection='column' width={['100%', '100%', '400px', '100%']} mx='auto'>
        {_.map(order.orders, (ord, i) => {
          const brandName = ord.product.brandName ?? ord.product.brand?.name;

          return (
            <OrderItem key={i} flexDirection='column' width='100%' mx='auto'>
              <Flex flexDirection='row' justifyContent='space-between' alignItems='flex-start'>
                {!_.isEmpty(brandName) && (
                  <NamesContainer>
                    <BrandName data-testid='item-brand'>{brandName}</BrandName>
                    <OrderName data-testid='item-name' hasBrand={!_.isEmpty(brandName)}>
                      {ord.product.Name}
                    </OrderName>
                  </NamesContainer>
                )}
                {_.isEmpty(brandName) && (
                  <OrderName data-test='item-name' hasBrand={!_.isEmpty(brandName)}>
                    {ord.product.Name}
                  </OrderName>
                )}
                <OrderQTYContainer flexDirection='row' width='99px' justifyContent='space-between' alignItems='center'>
                  <Text data-testid='item-quantity' fontSize='13px' color='#6d747b'>{`Qty: ${ord.quantity || 1}`}</Text>
                  <Text
                    data-testid='item-price'
                    fontSize='13px'
                    color='#6d747b'
                    fontWeight='bolder'
                  >{`$${ord.price.toFixed(2)}`}</Text>
                </OrderQTYContainer>
              </Flex>
              {isWeightedProduct(ord.product) && (
                <Text fontSize='13px' color='#6d747b' textAlign='left'>
                  {ord.option.replace('gC', 'g')}
                </Text>
              )}
            </OrderItem>
          );
        })}
      </Flex>
      <Divider width={['100%', '100%', '400px', '100%']} />
      <SubTotal order={order} />
      <AdditionalCharges order={{ ...order, checkoutLineItems }} rewardsDisplayName={rewardsDisplayName} />
      <Total order={order} />
    </>
  );
});

const NamesContainer = styled.div`
  display: block;
`;

const BrandName = styled.div`
  font-weight: bold;
  text-transform: uppercase;
  color: #949ea8;
  font-size: 12px;
  margin-bottom: 5px;
`;

const OrderName = styled(Text)`
  flex-shrink: 1;
  line-height: 15px;
  text-align: left;
  font-size: 13px;
  padding-right: 9px;
  color: #6d747b;
`;

export const Divider = styled.div`
  ${width}
  margin: 35px 0 32px 0;
  min-height: 1px;
  background-color: #d3dadf;
  @media ${mediaQueries.tablet} {
    margin: 32px auto 29px;
  }
`;

const OrderItem = styled(Flex)`
  &:not(:last-of-type) {
    margin-bottom: 23px;
  }
`;

const AddCharge = styled(Flex)`
  &:not(:last-of-type) {
    margin-bottom: 15px;
  }
`;

const LogoContainer = styled(Flex)`
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  @media ${mediaQueries.tablet} {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const Logo = styled(Imgix)`
  height: 69px;
  margin-right: 15px;
  cursor: pointer;
  object-fit: contain;
  object-position: 0 50%;
  @media ${mediaQueries.tablet} {
    margin-right: 0px;
    margin-bottom: 15px;
  }
`;

const OrderQTYContainer = styled(Flex)`
  flex-shrink: 0;
`;
